import React from 'react';
import { CheckCircle2 } from 'lucide-react';
import { TAG_TONE, Tone } from './dashboardTones';

/**
 * Gemeinsame Bausteine des Cockpits. Eine Tiefe je Element: die Kacheln
 * tragen einen Rahmen und keinen Schatten; Zustandsfarbe nur dort, wo sie
 * etwas bedeutet (rot = ueberfaellig, bernstein = heute/offen, gruen = frei).
 */

export type { Tone } from './dashboardTones';

export const Tag: React.FC<{ tone?: Tone; children: React.ReactNode; className?: string; title?: string }> = ({ tone = 'slate', children, className = '', title }) => (
    <span title={title} className={`inline-flex items-center gap-1 text-[11px] font-semibold leading-none px-1.5 py-1 rounded-md border whitespace-nowrap ${TAG_TONE[tone]} ${className}`}>
        {children}
    </span>
);

interface PanelProps extends React.HTMLAttributes<HTMLDivElement> {
    ref?: React.Ref<HTMLDivElement>;
}

export const Panel: React.FC<PanelProps> = ({ className = '', children, ...rest }) => (
    <section {...rest} className={`bg-white rounded-xl border border-slate-200 flex flex-col min-w-0 ${className}`}>
        {children}
    </section>
);

interface PanelHeaderProps {
    icon?: React.ReactNode;
    title: string;
    /** Zahl rechts neben dem Titel; mit `countTone` gefaerbt, sonst grau. */
    count?: number | null;
    countTone?: Tone;
    sub?: string;
    actions?: React.ReactNode;
    className?: string;
}

export const PanelHeader: React.FC<PanelHeaderProps> = ({ icon, title, count, countTone = 'slate', sub, actions, className = '' }) => (
    <div className={`px-3.5 py-2.5 border-b border-slate-100 flex items-center justify-between gap-3 shrink-0 ${className}`}>
        <div className="min-w-0 flex items-center gap-2">
            {icon && <span className="text-slate-400 shrink-0 [&>svg]:w-4 [&>svg]:h-4">{icon}</span>}
            <h2 className="text-[13px] font-semibold text-slate-800 truncate">{title}</h2>
            {typeof count === 'number' && (
                <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded-md border tabular ${TAG_TONE[count > 0 ? countTone : 'slate']}`}>{count}</span>
            )}
            {sub && <span className="text-[11px] text-slate-500 truncate hidden sm:inline">{sub}</span>}
        </div>
        {actions && <div className="flex items-center gap-1.5 shrink-0">{actions}</div>}
    </div>
);

/** Textknopf in Kopfzeilen und Fusszeilen („alle anzeigen", „Alle gelesen"). */
export const LinkButton: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement>> = ({ className = '', children, ...rest }) => (
    <button
        type="button"
        {...rest}
        className={`text-[11px] font-semibold text-blue-700 hover:text-blue-800 hover:underline underline-offset-2 rounded px-1 py-0.5 disabled:opacity-50 disabled:no-underline ${className}`}
    >
        {children}
    </button>
);

/** Kleiner Symbolknopf (Kopfzeile). Auf Touch mindestens 44 px hoch. */
export const IconButton: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement> & { label: string }> = ({ label, className = '', children, ...rest }) => (
    <button
        type="button"
        aria-label={label}
        title={label}
        {...rest}
        className={`inline-flex items-center justify-center w-8 h-8 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-800 transition-colors disabled:opacity-50 ${className}`}
    >
        {children}
    </button>
);

interface EmptyStateProps {
    title: string;
    hint?: string;
    /** `ok` = gruener Haken („alles erledigt"), `neutral` = nur Text. */
    tone?: 'ok' | 'neutral';
    icon?: React.ReactNode;
    className?: string;
}

export const EmptyState: React.FC<EmptyStateProps> = ({ title, hint, tone = 'neutral', icon, className = '' }) => (
    <div className={`flex flex-col items-center justify-center text-center px-4 py-6 ${className}`}>
        {tone === 'ok' ? (
            <div className="w-9 h-9 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center mb-2">
                <CheckCircle2 className="w-4.5 h-4.5 text-emerald-600" />
            </div>
        ) : icon ? (
            <div className="w-9 h-9 rounded-full bg-slate-50 border border-slate-200 flex items-center justify-center mb-2 text-slate-400 [&>svg]:w-4 [&>svg]:h-4">{icon}</div>
        ) : null}
        <p className={`text-xs font-semibold ${tone === 'ok' ? 'text-emerald-700' : 'text-slate-600'}`}>{title}</p>
        {hint && <p className="text-[11px] text-slate-500 mt-0.5 max-w-[28ch]">{hint}</p>}
    </div>
);

/** Ladeflaeche in der Form der spaeteren Zeilen — kein Sprung beim Eintreffen. */
export const SkeletonRows: React.FC<{ rows?: number; className?: string }> = ({ rows = 3, className = '' }) => (
    <div className={`px-3.5 py-2 space-y-2 ${className}`} aria-hidden="true">
        {Array.from({ length: rows }).map((_, i) => (
            <div key={i} className="flex items-center gap-3 py-1.5">
                <div className="skeleton w-9 h-9 rounded-lg shrink-0" />
                <div className="flex-1 space-y-1.5">
                    <div className="skeleton h-3 w-2/3" />
                    <div className="skeleton h-2.5 w-1/2" />
                </div>
            </div>
        ))}
    </div>
);

/** Projektzeile: Nummer mono, Name fett — ueberall im Cockpit gleich gesetzt. */
export const ProjectLabel: React.FC<{ number?: string | null; name?: string | null; className?: string; muted?: boolean }> = ({ number, name, className = '', muted }) => (
    <span className={`min-w-0 flex items-baseline gap-1.5 ${className}`}>
        {number && <span className={`text-[11px] font-mono shrink-0 ${muted ? 'text-slate-400' : 'text-slate-500'}`}>{number}</span>}
        <span className={`text-xs font-semibold truncate ${muted ? 'text-slate-500' : 'text-slate-800'}`}>{name || '—'}</span>
    </span>
);
